// Derives a user's capability flags for a factory from its `permission` array.
// A factory access level is one of ADMIN / EDIT / VALIDATE / VIEW (see Manage
// Access). Report pages use these to gate viewing, validating and editing:
//   - isAdmin    : can view ALL reports (any person/date) — ADMIN, EDIT, VALIDATE
//   - canValidate: can validate / reject reports          — ADMIN, VALIDATE
//   - readOnly   : never blocks submitting — a "View & Validate" user can still
//                  submit the reports assigned to them in Factory → Teams (the
//                  backend restricts saves to those report types), so they are
//                  NOT read-only. Kept as `false` for report pages that check it.
//   - canConfig  : can configure the factory               — ADMIN only
// Whether the current user may validate/reject a specific report.
//   - Admins validate everything.
//   - "View & Validate" users are limited by the factory's Validation config:
//     the report type must be in their `reports`, and (when a submitter is
//     known) the submitter must be in their `users`.
//   - Everyone else: no.
export function canValidateReport(
  selectedFactory: any,
  currentUser: any,
  reportType: string,
  submitterId?: any
): boolean {
  const perm: string[] = (selectedFactory && selectedFactory.permission) || []
  if (perm.includes("ADMIN")) return true
  if (!perm.includes("VALIDATE")) return false
  const uid = currentUser && (currentUser._id || currentUser.id)
  const cfg = ((selectedFactory && selectedFactory.validationConfig) || [])
    .find((c: any) => String(c.userId) === String(uid))
  if (!cfg) return false
  if (!((cfg.reports || []).includes(reportType))) return false
  if (submitterId !== undefined && submitterId !== null && submitterId !== "") {
    return (cfg.users || []).map((u: any) => String(u)).includes(String(submitterId))
  }
  return true
}

export function accessFlags(selectedFactory: any) {
  const perm: string[] = (selectedFactory && selectedFactory.permission) || []
  const isFactoryAdmin = perm.includes("ADMIN")
  const isEditor = perm.includes("EDIT")
  const isValidator = perm.includes("VALIDATE")
  return {
    // "Admin" report view = anyone who can see every report in the factory.
    isAdmin: isFactoryAdmin || isEditor || isValidator,
    canValidate: isFactoryAdmin || isValidator,
    // A pure "View & Validate" user (not Admin/Edit) is read-only on SHARED
    // reports (Daily Monitoring, Monthly Monitoring, Document Reviews, Health &
    // Safety) — they may only view + validate those. Their only editable report
    // is their OWN Daily Work (handled separately via validatorOnly on that page).
    readOnly: isValidator && !isFactoryAdmin && !isEditor,
    // Same flag, used where a page needs the "submit only my own" rule.
    validatorOnly: isValidator && !isFactoryAdmin && !isEditor,
    canConfig: isFactoryAdmin,
  }
}
